import React, { useContext, useState } from 'react'
import { FormBuilder } from '@tf/formbuilder'
import appConfig from 'app.config'
import { useTranslation } from 'react-i18next'

import SspContext from 'selfServicePortal/contexts/SspContext'
import {
	ISupportDataSsp,
	ProductType,
} from 'selfServicePortal/interfaces/i-api-support'
import { Heading } from '../../../../components/index'
import sendEmailSspService from '../../../../services/SupportServices/sendEmailSspService'

import {
	CustomerForm,
	getErrorWithFullMessage,
	getLanguageLocale,
	getMarket,
} from 'shared'
import { FormFactory } from 'shared/formBuilder/formFactory'
import {
	CountryValuesProductType,
	ICountryValues,
} from 'shared/constants/countryValueTypes'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import './Contact.scss'

const Contact = () => {
	const { t } = useTranslation()
	const {
		isCard,
		customerData,
		success,
		setSuccess,
		staticCountryValues,
		productType,
	} = useContext(SspContext)
	const [error, setError] = useState<Error>()
	const [loading, setLoading] = useState(false)

	const formSchema = FormFactory(CustomerForm.ContactForm, {
		formName: 'customerData',
		blockName: 'customerData',
		market: getMarket(),
		locale: getLanguageLocale(),
		getDataUrl: '',
		validationErrorList: false,
		requiredMessage: t('ValidateRequired'),
		submitButtonText: t('Send'),
	})

	const submit = async (info: ISupportDataSsp) => {
		setLoading(true)
		info.productType = isCard?.isCard
			? ProductType.Card
			: ProductType.Deposit

		try {
			await sendEmailSspService({
				...info,
				countryCode: appConfig.country,
				emailFrom: customerData?.email!,
				subject: `[SSP][${appConfig.country}] ${info.subject}`,
			})
			setSuccess({ ...success, contactForm: true })
			setLoading(false)
		} catch (e: any) {
			setLoading(false)
			setError(getErrorWithFullMessage(e))
		}
	}

	interface IServiceProps {
		val: ICountryValues
	}

	const ServiceInformationGermanyAustria = ({ val }: IServiceProps) => {
		return (
			<div className="outer-container">
				{val.productSpecific.map((value, key) => {
					if (value.productType === productType) {
						return (
							<div key={'contact-information-' + key}>
								<div className="title">{`${t(
									'CustomerService',
								)} ${
									value.productType ===
									CountryValuesProductType.Card
										? t('MastercardGold')
										: value.productType ===
										    CountryValuesProductType.Deposit_Fixed
										  ? t(
													'CustomerServicesDepositFixedAccount',
										    )
										  : t('CustomerServicesDepositAccount')
								}`}</div>
								{value.contactInformation.email ? (
									<div className="label email">
										<span className="envelope"></span>
										{value.contactInformation.email}
									</div>
								) : null}
								<div className="label">
									<FontAwesomeIcon
										icon="clock"
										className="contact-service-icon clock"
									/>
									{value.contactInformation.openingHours}
								</div>
								<div className="contact-address-information">
									<div>
										<FontAwesomeIcon
											icon="paper-plane"
											className="contact-service-icon"
										/>
										{val.tfFullName}
									</div>
									<div>{val.address.addressLine1}</div>
									<div>{val.address.zipCodeAndCity}</div>
									<div>{val.address.country}</div>
								</div>
								{productType ===
									CountryValuesProductType.Deposit && (
									<>
										<div className="label">
											Kontoverbindung
										</div>
										<div className="bank-data">{`${t(
											'IBAN',
										)}: ${value.bankInformation
											?.iban}`}</div>
										<div className="bank-data">{`${t(
											'BIC',
										)}: ${value.bankInformation
											?.bic}`}</div>
									</>
								)}
							</div>
						)
					} else return null
				})}
			</div>
		)
	}

	const ServiceInformationNorway = ({ val }: IServiceProps) => {
		return (
			<div className="outer-container">
				{val.productSpecific.map((value, key) => {
					if (value.productType === productType) {
						return (
							<div key={'contact-information-' + key}>
								<div className="title mb-3">{`${t(
									'CustomerService',
								)} ${
									value.productType ===
									CountryValuesProductType.Card
										? t('MastercardGold')
										: t('CustomerServicesDepositAccount')
								}`}</div>
								<div className="label email">
									<span className="envelope"></span>
									{value.contactInformation.email}
								</div>
								<a
									className="label"
									href={`tel:${value.contactInformation.phoneNumber}`}>
									<span className="phone"></span>
									{value.contactInformation.phoneNumber}
								</a>
								<div className="label">
									<FontAwesomeIcon
										icon="clock"
										className="contact-service-icon clock"
									/>
									{value.contactInformation.openingHours}
								</div>
								<div className="contact-address-information">
									<div>
										<FontAwesomeIcon
											icon="paper-plane"
											className="contact-service-icon"
										/>
										{val.tfFullName}
									</div>
									<div>{val.address.addressLine1}, </div>
									<div>{val.address.zipCodeAndCity}</div>
								</div>
							</div>
						)
					} else return null
				})}
			</div>
		)
	}

	return (
		<div className="fade-in contact container">
			<div className="fixed-width my-5">
				<div className="contact-service-information">
					<Heading title="Contact" />
					{staticCountryValues !== undefined ? (
						staticCountryValues?.country === 'no' ? (
							<ServiceInformationNorway
								val={staticCountryValues}
							/>
						) : (
							<ServiceInformationGermanyAustria
								val={staticCountryValues}
							/>
						)
					) : null}
				</div>
				{success.contactForm && (
					<Heading
						title="ContactFormSendSuccess"
						description="ContactFormSendMessage"
					/>
				)}
				{loading && <div id="circle-spinner"></div>}
				{!success.contactForm && !loading && (
					<>
						<Heading title="ContactForm" />
						<FormBuilder
							formSchema={formSchema!}
							apiError={undefined}
							showErrorsInForm={true}
							showSubmitButton={true}
							onSubmit={(values) => submit(values)}
						/>
					</>
				)}
				{error && !loading && !success && (
					<div className="error-msg">{error.message}</div>
				)}
			</div>
		</div>
	)
}
export default Contact
